import { useEffect, useState } from "react";
import { MagnifyingGlass, BookOpen, PlayCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";

export default function Help() {
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/help").then((r) => setArticles(r.data.items));
  }, []);

  const replayTour = () => {
    localStorage.removeItem("ciq_onboarding_done");
    toast.success("Tour reset. Refreshing…");
    setTimeout(() => window.location.assign("/dashboard"), 500);
  };

  const categories = ["All", ...Array.from(new Set(articles.map((a) => a.category)))];
  const filtered = articles.filter(
    (a) =>
      (cat === "All" || a.category === cat) &&
      (q.trim() === "" || (a.title + a.body).toLowerCase().includes(q.toLowerCase())),
  );

  return (
    <div className="animate-fade-up">
      <PageHeader guideId="help"
        overline="Resources"
        title="Help Centre"
        subtitle="Documentation, methodology notes, and walkthroughs for every CarbonIQ module."
        kicker={`${articles.length} ARTICLES · ${categories.length - 1} CATEGORIES`}
        action={
          <button
            data-testid="replay-tour-btn"
            onClick={replayTour}
            className="flex items-center gap-2 bg-gold text-black px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#e0bc40]"
          >
            <PlayCircle size={14} weight="fill" /> Replay onboarding tour
          </button>
        }
      />

      <section className="px-8 pt-8 grid md:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 theme-ink-dim" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="help-search"
              className="w-full theme-input border pl-9 pr-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-gold"
              placeholder="Search docs…"
            />
          </div>
          <div className="overline pt-3">Categories</div>
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`help-cat-${c}`}
              onClick={() => setCat(c)}
              className={`w-full text-left px-3 py-2 text-sm border rounded-sm transition-colors ${
                cat === c ? "border-gold bg-gold/10 theme-ink" : "theme-border theme-ink-muted hover:border-gold/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {filtered.map((a) => (
            <div key={a.id} className="border theme-border theme-surface">
              <button
                data-testid={`help-article-${a.id}`}
                onClick={() => setOpen(open === a.id ? null : a.id)}
                className="w-full text-left px-6 py-5 flex items-baseline justify-between hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
              >
                <div className="flex items-baseline gap-3">
                  <BookOpen size={14} weight="duotone" className="text-gold shrink-0 translate-y-0.5" />
                  <div>
                    <div className="font-serif-display text-xl theme-ink">{a.title}</div>
                    <div className="text-[11px] theme-ink-dim mt-0.5 uppercase tracking-widest">{a.category}</div>
                  </div>
                </div>
                <span className="text-xs theme-ink-dim font-mono-data">{open === a.id ? "−" : "+"}</span>
              </button>
              {open === a.id && (
                <div className="px-6 pb-6 text-sm theme-ink-muted leading-relaxed whitespace-pre-line">
                  {a.body}
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="border theme-border theme-surface p-12 text-center theme-ink-dim">
              No articles match.
            </div>
          )}
        </div>
      </section>
      <div className="pb-12" />
    </div>
  );
}
